import React, { useCallback, useRef, useState } from "react";
import { ChevronsLeftRight, Sparkles } from "lucide-react";

/**
 * BeforeAfterSlider — draggable divider that reveals `enhancedSrc` on top of
 * `originalSrc`. Used to show the customer the scanner-quality enhancement
 * before they pay to print.
 *
 * Props:
 *   originalSrc  string  — data URL / http URL of the raw upload
 *   enhancedSrc  string  — data URL / http URL of the /enhance-preview result
 *   height       number  — pixel height of the frame (default 380)
 *   initial      number  — initial divider position 0..100 (default 50)
 */
export default function BeforeAfterSlider({
  originalSrc,
  enhancedSrc,
  height = 380,
  initial = 50,
}) {
  const [pos, setPos] = useState(initial);
  const frameRef = useRef(null);
  const dragging = useRef(false);

  const updateFromClientX = useCallback((clientX) => {
    const frame = frameRef.current;
    if (!frame) return;
    const rect = frame.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPos(pct);
  }, []);

  const onDown = (e) => {
    dragging.current = true;
    const pt = e.touches ? e.touches[0] : e;
    updateFromClientX(pt.clientX);
    e.preventDefault?.();
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    const pt = e.touches ? e.touches[0] : e;
    updateFromClientX(pt.clientX);
  };
  const onUp = () => { dragging.current = false; };

  return (
    <div className="w-full">
      <div
        ref={frameRef}
        className="relative w-full rounded-lg overflow-hidden bg-black select-none cursor-ew-resize border border-border"
        style={{ height }}
        onMouseDown={onDown}
        onMouseMove={onMove}
        onMouseUp={onUp}
        onMouseLeave={onUp}
        onTouchStart={onDown}
        onTouchMove={onMove}
        onTouchEnd={onUp}
        data-testid="before-after-slider"
      >
        {/* Original (base layer) */}
        {originalSrc && (
          <img
            src={originalSrc}
            alt="original"
            className="absolute inset-0 w-full h-full object-contain pointer-events-none"
            draggable={false}
          />
        )}
        {/* Enhanced (clipped from the left) */}
        {enhancedSrc && (
          <div
            className="absolute inset-0 pointer-events-none overflow-hidden"
            style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
          >
            <img
              src={enhancedSrc}
              alt="enhanced"
              className="w-full h-full object-contain"
              draggable={false}
            />
          </div>
        )}

        {/* Labels */}
        <div className="absolute top-3 left-3 px-2 py-1 rounded-md bg-black/60 text-white text-[10px] uppercase tracking-widest font-semibold pointer-events-none">
          Before
        </div>
        <div className="absolute top-3 right-3 px-2 py-1 rounded-md bg-brand-green text-white text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1 pointer-events-none">
          <Sparkles size={10} /> After
        </div>

        {/* Divider bar + drag handle */}
        <div
          className="absolute top-0 bottom-0 w-[2px] bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.4)] pointer-events-none"
          style={{ left: `${pos}%`, transform: "translateX(-1px)" }}
        >
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white shadow-lg flex items-center justify-center text-slate-900"
          >
            <ChevronsLeftRight size={16} />
          </div>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mt-2 text-center">
        Drag the divider to compare. What you see on the right is what will print.
      </p>
    </div>
  );
}
